import type { SpreadsheetSelectionSnapshot } from 'components/ScalarDatasheet/ScalarDatasheet.props';
import type { SpreadsheetSelectionAction } from 'components/ScalarSpreadsheet/components/SelectionActionsBar/SelectionActionsBar.props';
import type { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import type { VirtualExclusionItem } from './persistVirtualExclusions';
import { CashFlowColumnAlias } from '../data/constants';
import type { CashFlowSourceKind, EvaluatedCashFlowLedgerCell } from '../data/types';

// Every cell of a row carries entityId and its virtual source; only Amount cells carry the exclusion state.
export type CflAmountCell = ICell &
  Partial<Pick<EvaluatedCashFlowLedgerCell, 'entityId' | 'is_virtual' | 'is_excludable' | 'source_kind' | 'source_id'>> & {
    setProps?: (props: Partial<ICell>) => void;
  };

const HEADER_ROW_NUMBER = 1;

/** Stable identity for selection tracking: saved id, else virtual source, else the positional key; always suffixed with the column legend. */
export const cflCellIdentity = (cell: ICell): string => {
  const { entityId, source_kind: sourceKind, source_id: sourceId, columnLegend, key, rowNumber } = cell as CflAmountCell;
  if (entityId != null) {
    return `${entityId}|${columnLegend}`;
  }
  if (sourceKind && sourceId != null) {
    return `${sourceKind}:${sourceId}|${columnLegend}`;
  }
  return key ?? `${columnLegend}${rowNumber}`;
};

export interface CflSelectedRow {
  rowIndex: number;
  amountCell: CflAmountCell;
  isVirtual: boolean;
  isExcludable: boolean;
  isEnabled: boolean;
  entityId?: number | null;
  sourceKind?: CashFlowSourceKind | null;
  sourceId?: number | null;
}

const isAmountCell = (cell: ICell) => cell.alias === CashFlowColumnAlias.AMOUNT;

const toSelectedRow = (rowIndex: number, amountCell: CflAmountCell): CflSelectedRow => ({
  rowIndex,
  amountCell,
  isVirtual: !!amountCell.is_virtual,
  isExcludable: amountCell.is_excludable !== false,
  isEnabled: amountCell.enabled !== false,
  entityId: amountCell.entityId ?? null,
  sourceKind: amountCell.source_kind ?? null,
  sourceId: amountCell.source_id ?? null,
});

/**
 * Distinct ledger rows touched by a selection, resolved by sheet row number so column legends do not shift them.
 * Rows the sheet hides are never returned, so no bulk action can reach them.
 */
export const collectSelectedRows = (snapshot: SpreadsheetSelectionSnapshot | null, data: ICell[][]): CflSelectedRow[] => {
  if (!snapshot) {
    return [];
  }

  const amountByRowNumber = new Map<number, CflAmountCell>();
  data.flat().forEach(cell => {
    if (isAmountCell(cell) && cell.rowNumber != null && cell.isVisible !== false) {
      amountByRowNumber.set(cell.rowNumber, cell as CflAmountCell);
    }
  });

  const selectedRowNumbers = new Set(
    snapshot.cells
      .map(({ cell }) => cell.rowNumber)
      .filter((rowNumber): rowNumber is number => rowNumber != null && rowNumber > HEADER_ROW_NUMBER)
  );

  return [...selectedRowNumbers]
    .sort((a, b) => a - b)
    .flatMap(rowNumber => {
      const amountCell = amountByRowNumber.get(rowNumber);
      // Cash-flow data excludes the sheet's header, so its 1-based row is one less than the cell row number.
      return amountCell ? [toSelectedRow(rowNumber - HEADER_ROW_NUMBER, amountCell)] : [];
    });
};

export interface ExclusionTargets {
  manual: CflAmountCell[];
  virtual: VirtualExclusionItem[];
}

/** Splits rows into checkbox-style cell mutations (manual) and per-row PATCH items (virtual) for a target excluded state. */
export const partitionExclusionTargets = (rows: CflSelectedRow[], isExcluded: boolean): ExclusionTargets =>
  rows
    .filter(row => row.isExcludable && row.isEnabled === isExcluded)
    .reduce<ExclusionTargets>(
      (targets, { isVirtual, amountCell, sourceKind, sourceId }) => {
        if (!isVirtual) {
          targets.manual.push(amountCell);
        } else if (sourceKind && sourceId != null) {
          targets.virtual.push({ sourceKind, sourceId, isExcluded });
        }
        return targets;
      },
      { manual: [], virtual: [] }
    );

// Excludable rows split by their current state.
const splitExcludable = (rows: CflSelectedRow[]) => {
  const excludable = rows.filter(row => row.isExcludable);
  return { enabled: excludable.filter(row => row.isEnabled), excluded: excludable.filter(row => !row.isEnabled) };
};

export interface ExclusionToggle {
  rows: CflSelectedRow[];
  isExcluded: boolean;
}

/** Space shortcut target: disable the enabled excludable rows if any, otherwise enable the excluded ones; null when nothing is excludable. */
export const resolveSpaceToggle = (rows: CflSelectedRow[]): ExclusionToggle | null => {
  const { enabled, excluded } = splitExcludable(rows);
  if (enabled.length) {
    return { rows: enabled, isExcluded: true };
  }
  return excluded.length ? { rows: excluded, isExcluded: false } : null;
};

/** Bulk Delete target: the manual rows' sheet indexes, and how many fund-derived rows it keeps. */
export const splitDeletable = (rows: CflSelectedRow[]) => {
  const rowIndexes = rows.filter(row => !row.isVirtual).map(row => row.rowIndex);
  return { rowIndexes, kept: rows.length - rowIndexes.length };
};

export interface CflSelectionActionDeps {
  canEdit: boolean;
  onSetExcluded: (rows: CflSelectedRow[], isExcluded: boolean) => void;
  /** Unsaved rows are keyed by position, so the page clears the selection once rows are gone. */
  onDeleteRows: (rows: CflSelectedRow[], clearSelection: () => void) => void;
}

/** Actions for the selection bar: Disable (N) and Enable (M) over the excludable rows in each state, Delete (N) over manual rows. */
export const buildCflSelectionActions = (
  rows: CflSelectedRow[],
  { canEdit, onSetExcluded, onDeleteRows }: CflSelectionActionDeps
): SpreadsheetSelectionAction[] => {
  if (!canEdit || !rows.length) {
    return [];
  }
  const { enabled, excluded } = splitExcludable(rows);
  const deletable = splitDeletable(rows).rowIndexes.length;
  const exclusionAction = (key: string, verb: string, targets: CflSelectedRow[], isExcluded: boolean) =>
    targets.length ? [{ key, label: `${verb} (${targets.length})`, onClick: () => onSetExcluded(targets, isExcluded) }] : [];

  return [
    ...exclusionAction('disable-rows', 'Disable', enabled, true),
    ...exclusionAction('enable-rows', 'Enable', excluded, false),
    {
      key: 'delete-rows',
      tone: 'danger',
      label: `Delete (${deletable})`,
      disabled: !deletable,
      tooltip: deletable ? undefined : 'Derived rows cannot be deleted',
      onClick: (_snapshot, clearSelection) => onDeleteRows(rows, clearSelection),
    },
  ];
};
